import { useState, useMemo } from "react";
import { 
    ArrowRight, 
    ArrowLeft,
    DollarSign,
    Percent,
    Target,
    Zap,
    TrendingUp,
    Download,
    Calendar,
    Loader2,
    Users,
    Award,
    Gift,
    BarChart3,
    CheckCircle,
    Rocket,
    Clock,
    Gauge,
    Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import ServicesPromo from "./ServicesPromo";
import LeadCaptureGate from "./LeadCaptureGate";
import { downloadReferralPlanPDF } from "./utils/generateReferralPDF";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Growth speed configurations
const GROWTH_SPEEDS = {
    slow: {
        name: "Slow & Steady",
        description: "Conservative approach with lower risk",
        marginPercent: 10,
        icon: Clock,
        color: "emerald",
        payoutFrequency: "Monthly",
        tierMultipliers: [1, 1.25, 1.5]
    },
    moderate: {
        name: "Moderate Growth",
        description: "Balanced approach for steady scaling",
        marginPercent: 20,
        icon: TrendingUp,
        color: "blue",
        payoutFrequency: "Bi-weekly",
        tierMultipliers: [1, 1.5, 2]
    },
    aggressive: {
        name: "Aggressive",
        description: "Maximum growth with higher investment",
        marginPercent: 30,
        icon: Rocket,
        color: "purple",
        payoutFrequency: "Weekly",
        tierMultipliers: [1, 1.75, 2.5]
    }
};

export default function ReferralProgramBuilder({ onBack, user, subscription }) {
    const [step, setStep] = useState(1); // 1 = inputs, 2 = results
    const [isCalculating, setIsCalculating] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const [isPDFDownloaded, setIsPDFDownloaded] = useState(false);

    // Check if user has Pro/Agency access
    const isPaidUser = subscription?.tier === 'pro' || subscription?.tier === 'agency' || subscription?.tier === 'premium' ||
                       user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency' || user?.subscription_tier === 'premium';
    const isTestUser = user?.email === 'testpro@sitegradepro.com' || user?.user_id === 'test_pro_user_123';
    
    // Form inputs
    const [formData, setFormData] = useState({
        productPrice: "",
        profitMargin: "",
        monthlySalesGoal: "",
        growthSpeed: "moderate"
    });
    
    const [errors, setErrors] = useState({});

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.productPrice || parseFloat(formData.productPrice) <= 0) {
            newErrors.productPrice = "Please enter a valid price";
        }
        if (!formData.profitMargin || parseFloat(formData.profitMargin) <= 0 || parseFloat(formData.profitMargin) > 100) {
            newErrors.profitMargin = "Please enter a valid margin (1-100%)";
        }
        if (!formData.monthlySalesGoal || parseInt(formData.monthlySalesGoal) <= 0) {
            newErrors.monthlySalesGoal = "Please enter a valid sales goal";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Calculate results
    const results = useMemo(() => {
        const price = parseFloat(formData.productPrice) || 0;
        const margin = parseFloat(formData.profitMargin) || 0;
        const salesGoal = parseInt(formData.monthlySalesGoal) || 0;
        const speed = GROWTH_SPEEDS[formData.growthSpeed];
        
        if (!price || !margin || !salesGoal) return null;
        
        // Calculate profit per sale
        const profitPerSale = price * (margin / 100);
        
        // Calculate referral commission (% of profit margin based on growth speed)
        const baseCommission = profitPerSale * (speed.marginPercent / 100);
        
        // Calculate tier commissions
        const tiers = [
            {
                name: "Bronze",
                referrals: "1-5",
                commission: baseCommission * speed.tierMultipliers[0],
                description: "Entry level for new referrers"
            },
            {
                name: "Silver",
                referrals: "6-15",
                commission: baseCommission * speed.tierMultipliers[1],
                description: "For consistent performers"
            },
            {
                name: "Gold",
                referrals: "16+",
                commission: baseCommission * speed.tierMultipliers[2],
                description: "Top referrer rewards"
            }
        ];
        
        // Estimate referral-driven sales (assume 20-40% based on speed)
        const referralSalesPercent = speed.marginPercent + 10; // 20%, 30%, or 40%
        const estimatedReferralSales = Math.round(salesGoal * (referralSalesPercent / 100));
        
        // Calculate costs and ROI
        const avgCommission = (tiers[0].commission + tiers[1].commission + tiers[2].commission) / 3;
        const totalCommissionCost = estimatedReferralSales * avgCommission;
        const revenueFromReferrals = estimatedReferralSales * price;
        const profitFromReferrals = estimatedReferralSales * profitPerSale;
        const netProfit = profitFromReferrals - totalCommissionCost;
        const roi = totalCommissionCost > 0 ? ((netProfit / totalCommissionCost) * 100) : 0;
        
        return {
            baseCommission,
            tiers,
            profitPerSale,
            payoutFrequency: speed.payoutFrequency,
            growthSpeed: speed,
            estimatedReferralSales,
            totalCommissionCost,
            revenueFromReferrals,
            profitFromReferrals,
            netProfit,
            roi,
            price,
            margin,
            salesGoal
        };
    }, [formData]);

    const handleCalculate = () => {
        if (!validateForm()) return;
        
        setIsCalculating(true);
        setTimeout(() => {
            setIsCalculating(false);
            setStep(2);
        }, 1500);
    };

    const handleExportPDF = async () => {
        // Pro/Agency users or test users get free PDF download
        if (isPaidUser || isTestUser) {
            handleDirectDownload();
            toast.success("PDF downloaded! (Pro benefit)");
            return;
        }

        // Free users go through Stripe checkout
        setIsCheckingOut(true);
        try {
            const response = await axios.post(`${API}/checkout`, {
                plan_id: "referral_plan_pdf",
                origin_url: window.location.origin,
                email: leadData?.email || null,
                success_url_params: `tool=referral-builder&payment_success=true&plan_id=referral_plan_pdf`
            });
            window.location.href = response.data.url;
        } catch (error) {
            console.error("Checkout failed:", error);
            toast.error("Failed to start checkout. Please try again.");
            setIsCheckingOut(false);
        }
    };

    // Direct PDF download for Pro users
    const handleDirectDownload = async () => {
        try {
            const speed = GROWTH_SPEEDS[formData.growthSpeed];
            await downloadReferralPlanPDF({
                businessName: leadData?.name || "Your Business",
                websiteUrl: leadData?.website || "yourwebsite.com",
                inputs: {
                    price: parseFloat(formData.productPrice) || 0,
                    margin: parseFloat(formData.profitMargin) || 0,
                    salesGoal: parseInt(formData.monthlySalesGoal) || 0,
                    growthSpeed: speed
                },
                results: {
                    baseCommission: results?.baseCommission || 0,
                    tiers: results?.tiers || [],
                    score: results?.score || 0,
                    maxScore: results?.maxScore || 200,
                    estimatedRevenue: results?.monthlyRevenue || 0,
                    estimatedROI: results?.roi || 0
                },
                leadData
            });
            setIsPDFDownloaded(true);
            toast.success("Your Referral Program Blueprint has been downloaded!");
        } catch (error) {
            console.error("PDF generation failed:", error);
            toast.error("Failed to generate PDF.");
        }
    };

    const handleGetHelp = () => {
        if (onBack) {
            onBack("consultation");
        }
    };

    const resetBuilder = () => {
        setStep(1);
        setFormData({
            productPrice: "",
            profitMargin: "",
            monthlySalesGoal: "",
            growthSpeed: "moderate"
        });
        setErrors({});
    };

    const formatCurrency = (num) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num);
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a]">
            {/* Subtle grid background */}
            <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <button 
                        onClick={() => onBack && onBack()}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        data-testid="back-to-main-btn"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span className="text-sm">Back to SiteGradePro</span>
                    </button>
                    {step === 1 && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <BarChart3 className="h-4 w-4" />
                            <span>Data-Driven Recommendations</span>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 min-h-screen">
                <div className="max-w-4xl mx-auto">
                    
                    {/* Step 1: Input Form */}
                    {step === 1 && !isCalculating && (
                        <div className="animate-fade-in pt-8" data-testid="builder-inputs">
                            {/* Header */}
                            <div className="text-center mb-10">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-sm mb-6">
                                    <Users className="h-4 w-4" />
                                    Free Referral Strategy Tool
                                </div>
                                
                                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                                    Referral Program Builder
                                </h1>
                                
                                <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                                    Calculate the perfect referral commission structure to grow your business 
                                    while protecting your margins.
                                </p>
                            </div>

                            {/* Input Card */}
                            <Card className="bg-[#111111] border-white/10 mb-6">
                                <CardContent className="p-6 md:p-8 space-y-6">
                                    {/* Product Price */}
                                    <div>
                                        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                            <DollarSign className="h-4 w-4" />
                                            Service/Product Price
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                            <Input
                                                type="number"
                                                placeholder="e.g., 299"
                                                value={formData.productPrice}
                                                onChange={(e) => setFormData(prev => ({ ...prev, productPrice: e.target.value }))}
                                                className={`pl-8 bg-black/50 border-gray-800 text-white text-lg h-12 ${errors.productPrice ? 'border-red-500' : ''}`}
                                                data-testid="input-price"
                                            />
                                        </div>
                                        {errors.productPrice && (
                                            <p className="text-red-400 text-sm mt-1">{errors.productPrice}</p>
                                        )}
                                    </div>

                                    {/* Profit Margin */}
                                    <div>
                                        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                            <Percent className="h-4 w-4" />
                                            Estimated Profit Margin
                                        </label>
                                        <div className="relative">
                                            <Input
                                                type="number"
                                                placeholder="e.g., 40"
                                                value={formData.profitMargin}
                                                onChange={(e) => setFormData(prev => ({ ...prev, profitMargin: e.target.value }))}
                                                className={`pr-8 bg-black/50 border-gray-800 text-white text-lg h-12 ${errors.profitMargin ? 'border-red-500' : ''}`}
                                                data-testid="input-margin"
                                            />
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                                        </div>
                                        {errors.profitMargin && (
                                            <p className="text-red-400 text-sm mt-1">{errors.profitMargin}</p>
                                        )}
                                    </div>

                                    {/* Monthly Sales Goal */}
                                    <div>
                                        <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                            <Target className="h-4 w-4" />
                                            Monthly Sales Goal (units)
                                        </label>
                                        <Input
                                            type="number"
                                            placeholder="e.g., 100"
                                            value={formData.monthlySalesGoal}
                                            onChange={(e) => setFormData(prev => ({ ...prev, monthlySalesGoal: e.target.value }))}
                                            className={`bg-black/50 border-gray-800 text-white text-lg h-12 ${errors.monthlySalesGoal ? 'border-red-500' : ''}`}
                                            data-testid="input-sales-goal"
                                        />
                                        {errors.monthlySalesGoal && (
                                            <p className="text-red-400 text-sm mt-1">{errors.monthlySalesGoal}</p>
                                        )}
                                    </div>

                                    {/* Growth Speed Selection */}
                                    <div>
                                        <label className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                                            <Gauge className="h-4 w-4" />
                                            Desired Referral Growth Speed
                                        </label>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            {Object.entries(GROWTH_SPEEDS).map(([key, speed]) => {
                                                const Icon = speed.icon;
                                                const isSelected = formData.growthSpeed === key;
                                                return (
                                                    <button
                                                        key={key}
                                                        onClick={() => setFormData(prev => ({ ...prev, growthSpeed: key }))}
                                                        className={`p-4 rounded-lg border text-left transition-all ${
                                                            isSelected 
                                                                ? `border-${speed.color}-500 bg-${speed.color}-500/10` 
                                                                : 'border-white/10 hover:border-white/20 bg-black/30'
                                                        }`}
                                                        data-testid={`speed-${key}`}
                                                    >
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Icon className={`h-5 w-5 ${isSelected ? `text-${speed.color}-400` : 'text-gray-500'}`} />
                                                            <span className={`font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                                                                {speed.name}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-500">{speed.description}</p>
                                                        <div className="mt-2 text-xs text-gray-400">
                                                            {speed.marginPercent}% of margin
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Calculate Button */}
                            <Button
                                onClick={handleCalculate}
                                size="lg"
                                className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-violet-500/20"
                                data-testid="calculate-btn"
                            >
                                Build My Referral Program
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>

                            <p className="text-center text-sm text-gray-500 mt-4">
                                Free instant analysis • No email required
                            </p>
                        </div>
                    )}

                    {/* Calculating Animation */}
                    {isCalculating && (
                        <div className="text-center pt-32 animate-fade-in" data-testid="calculating-state">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/20 flex items-center justify-center mx-auto mb-8">
                                <Loader2 className="h-10 w-10 text-violet-400 animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-3">
                                Building Your Referral Program...
                            </h2>
                            <p className="text-gray-400">
                                Calculating optimal commission structure
                            </p>
                        </div>
                    )}

                    {/* Step 2: Results */}
                    {step === 2 && !isCalculating && results && (
                        <div className="animate-fade-in pt-8" data-testid="builder-results">
                            {/* Lead Capture Gate */}
                            {!isUnlocked ? (
                                <LeadCaptureGate
                                    score={Math.round(results.roi)}
                                    maxScore={200}
                                    toolName="Referral Program Builder"
                                    previewContent={
                                        <div className="space-y-2">
                                            <p className="text-white font-semibold">Base Commission: {formatCurrency(results.baseCommission)}</p>
                                            <p className="text-sm text-gray-500">Unlock to see full tier structure & ROI breakdown</p>
                                        </div>
                                    }
                                    onUnlock={(data) => {
                                        setLeadData(data);
                                        setIsUnlocked(true);
                                    }}
                                />
                            ) : (
                            <>
                            {/* Results Header */}
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm mb-4">
                                    <CheckCircle className="h-4 w-4" />
                                    Program Ready
                                </div>
                                <h2 className="text-3xl font-bold text-white mb-2">
                                    Your Referral Program Strategy
                                </h2>
                                <p className="text-gray-400">
                                    Based on {formatCurrency(results.price)} product with {results.margin}% margin
                                </p>
                            </div>

                            {/* Key Metrics */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                <Card className="bg-[#111111] border-white/10">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Base Commission</p>
                                        <p className="text-2xl font-bold text-violet-400">{formatCurrency(results.baseCommission)}</p>
                                        <p className="text-xs text-gray-500">per referral</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-[#111111] border-white/10">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payout Frequency</p>
                                        <p className="text-2xl font-bold text-blue-400">{results.payoutFrequency}</p>
                                        <p className="text-xs text-gray-500">recommended</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-[#111111] border-white/10">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Est. Referral Sales</p>
                                        <p className="text-2xl font-bold text-emerald-400">{results.estimatedReferralSales}</p>
                                        <p className="text-xs text-gray-500">per month</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-[#111111] border-white/10">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Expected ROI</p>
                                        <p className="text-2xl font-bold text-amber-400">{results.roi.toFixed(0)}%</p>
                                        <p className="text-xs text-gray-500">return</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* 3-Tier Structure */}
                            <Card className="bg-[#111111] border-white/10 mb-6">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-2 mb-6">
                                        <Award className="h-5 w-5 text-violet-400" />
                                        <h3 className="text-lg font-semibold text-white">Recommended 3-Tier Structure</h3>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {results.tiers.map((tier, index) => {
                                            const colors = ['amber', 'gray', 'yellow'];
                                            const bgColors = ['bg-amber-500/10', 'bg-gray-500/10', 'bg-yellow-500/10'];
                                            const borderColors = ['border-amber-500/30', 'border-gray-400/30', 'border-yellow-500/30'];
                                            const textColors = ['text-amber-400', 'text-gray-300', 'text-yellow-400'];
                                            
                                            return (
                                                <div 
                                                    key={tier.name}
                                                    className={`p-5 rounded-lg border ${borderColors[index]} ${bgColors[index]}`}
                                                >
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <Gift className={`h-5 w-5 ${textColors[index]}`} />
                                                        <span className={`font-bold ${textColors[index]}`}>{tier.name}</span>
                                                    </div>
                                                    <div className="mb-3">
                                                        <p className="text-3xl font-bold text-white">{formatCurrency(tier.commission)}</p>
                                                        <p className="text-sm text-gray-500">per successful referral</p>
                                                    </div>
                                                    <div className="pt-3 border-t border-white/10">
                                                        <p className="text-sm text-gray-400">
                                                            <span className="text-white font-medium">{tier.referrals}</span> referrals/month
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">{tier.description}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Financial Breakdown */}
                            <Card className="bg-[#111111] border-white/10 mb-6">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-2 mb-6">
                                        <BarChart3 className="h-5 w-5 text-violet-400" />
                                        <h3 className="text-lg font-semibold text-white">Estimated Monthly Impact</h3>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        {/* Revenue Bar */}
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-gray-400">Revenue from Referrals</span>
                                                <span className="text-emerald-400 font-medium">{formatCurrency(results.revenueFromReferrals)}</span>
                                            </div>
                                            <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Commission Cost Bar */}
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-gray-400">Total Commission Cost</span>
                                                <span className="text-red-400 font-medium">-{formatCurrency(results.totalCommissionCost)}</span>
                                            </div>
                                            <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full"
                                                    style={{ width: `${(results.totalCommissionCost / results.revenueFromReferrals) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Net Profit Bar */}
                                        <div className="pt-4 border-t border-white/10">
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-white font-medium">Net Profit from Referrals</span>
                                                <span className="text-violet-400 font-bold text-lg">{formatCurrency(results.netProfit)}</span>
                                            </div>
                                            <div className="h-4 bg-white/5 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
                                                    style={{ width: `${(results.netProfit / results.revenueFromReferrals) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Summary Stats */}
                                    <div className="mt-6 p-4 bg-white/5 rounded-lg">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <p className="text-xs text-gray-500">Profit per Sale</p>
                                                <p className="text-lg font-bold text-white">{formatCurrency(results.profitPerSale)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Commission Rate</p>
                                                <p className="text-lg font-bold text-white">{results.growthSpeed.marginPercent}%</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Break-even Sales</p>
                                                <p className="text-lg font-bold text-white">{Math.ceil(results.totalCommissionCost / results.profitPerSale)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Program Settings Summary */}
                            <Card className="bg-[#111111] border-white/10 mb-8">
                                <CardContent className="p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Program Settings</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <p className="text-gray-500">Growth Strategy</p>
                                            <p className="text-white font-medium">{results.growthSpeed.name}</p>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <p className="text-gray-500">Payout Schedule</p>
                                            <p className="text-white font-medium">{results.payoutFrequency}</p>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <p className="text-gray-500">Cookie Duration</p>
                                            <p className="text-white font-medium">30 days</p>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <p className="text-gray-500">Min. Payout</p>
                                            <p className="text-white font-medium">{formatCurrency(results.baseCommission * 3)}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* CTAs */}
                            <div className="space-y-4">
                                {/* Pro user badge */}
                                {(isPaidUser || isTestUser) && (
                                    <div className="flex items-center justify-center gap-2 text-violet-400 text-sm mb-2">
                                        <Crown className="h-4 w-4" />
                                        <span>Pro Benefit: Free PDF Download</span>
                                    </div>
                                )}
                                
                                {isPDFDownloaded ? (
                                    <>
                                        <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                                            <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                                            <p className="text-emerald-400 font-semibold">PDF Downloaded!</p>
                                        </div>
                                        <Button
                                            onClick={handleDirectDownload}
                                            size="lg"
                                            className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-violet-500/20"
                                        >
                                            <Download className="mr-2 h-5 w-5" />
                                            Download Again
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        onClick={handleExportPDF}
                                        disabled={isCheckingOut}
                                        size="lg"
                                        className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-violet-500/20"
                                        data-testid="export-pdf-btn"
                                    >
                                        {isCheckingOut ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Processing...
                                            </>
                                        ) : (isPaidUser || isTestUser) ? (
                                            <>
                                                <Download className="mr-2 h-5 w-5" />
                                                Export Referral Plan PDF
                                            </>
                                        ) : (
                                            <>
                                                <Download className="mr-2 h-5 w-5" />
                                                Export Referral Plan PDF — $19
                                            </>
                                        )}
                                    </Button>
                                )}
                                
                                <Button
                                    onClick={handleGetHelp}
                                    variant="outline"
                                    size="lg"
                                    className="w-full border-white/20 text-white hover:bg-white/5 hover:border-white/40 py-6 text-lg rounded-lg"
                                    data-testid="get-help-btn"
                                >
                                    <Calendar className="mr-2 h-5 w-5" />
                                    Get Implementation Help
                                </Button>
                            </div>

                            {/* Recalculate */}
                            <button
                                onClick={resetBuilder}
                                className="w-full mt-6 text-sm text-gray-500 hover:text-white transition-colors"
                                data-testid="recalculate-btn"
                            >
                                Recalculate with different values
                            </button>

                            {/* Services Promo */}
                            <ServicesPromo onBookService={(serviceId) => {
                                if (onBack) onBack("consultation");
                            }} />
                            </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
